import { Module, GetterTree, MutationTree, ActionTree, ActionContext } from 'vuex';
import { SkypeConv, SkypePagination, SkypeState, SkypeMsgGroup } from '@/store/modules/skype/types';
import { skypeAPI, GetSkypeConvsReq } from '@/api/skype';
import { RootState } from '@/store/types';


export const moduleName = "skype";

export enum SkypeGetters {
    getSkypeUserList = "GET_SKYPE_USER_LIST",
    getIsUsersLoading = "GET_IS_USERS_LOADING",
    getSkypeConvs = "GET_SKYPE_CONVS",
    getConvsLoading = "GET_CONVS_LOADING",
    getMsgsLoading = "GET_MSG_LOADING",
    getConvsPagination = "GET_CONVS_PAGINATION",
    getIsAllConvsLoaded = "GET_IS_ALL_CONVS_LOADED",
    getSelectedConv = "GET_SELECTED_CONV",
    getMsgGroupByConvId = "GET_MSG_GROUP_BY_CONV_ID",
    getMsgsPaginationByConvId = "GET_MSGS_PAGINATION_BY_CONV_ID",
    getMsgsAllLoadedByConvId = "GET_MSGS_ALL_LOADED_BY_CONV_ID",
}

export enum SkypeMutations {
    setSkypeUserList = "SET_SKYPE_USER_LIST",
    setIsUsersLoading = "SET_IS_USERs_LOADING",
    setSkypeConvs = "SET_SKYPE_CONVS",
    setConvsLoading = "SET_CONVS_LOADING",
    setMsgsLoading = "SET_MSG_LOADING",
    setConvsPagination = "SET_CONVS_PAGINATION",
    setIsAllConvsLoaded = "SET_IS_ALL_CONVS_LOADED",
    setSelectedConv = "SET_SELECTED_CONV",
    setMsgsByConvId = "SET_MSGS_BY_CONV_ID",
    setMsgsPaginationByConvId = "SET_MSGS_PAGINATION_BY_CONV_ID",
    setMsgsAllLoadedByConvId = "SET_MSGS_ALL_LOADED_BY_CONV_ID",
    resetAll = "RESET_ALL",
}

export enum SkypeActions {
    asyncGetSkypeUserList = "ASYNC_GET_SKYPE_USER_LIST",
    asyncGetSkypeConvs = "ASYNC_GET_SKYPE_CONVS",
    asyncGetSkypeMsgs = "ASYNC_GET_SKYPE_MSGS",
}

const defaultPagination: SkypePagination = {
    page: 1,
    limit: 10,
}

const defaultMsgsGroup: SkypeMsgGroup = {
    isGroup: false,
    msgs: [],
}

const defaultState: SkypeState = {
    users: [],
    isUsersLoading: false,
    convs: [],
    isConvsLoading: false,
    isMsgsLoading: false,
    selectedConv: "",
    isAllConvsLoaded: false,
    msgs: {},
    convsPagination: defaultPagination,
}

const state: SkypeState = { ...defaultState };

export const getters: GetterTree<SkypeState, RootState> = {
    [SkypeGetters.getSkypeConvs](state): SkypeConv[] {
        return state.convs;
    },
    [SkypeGetters.getConvsLoading](state): boolean {
        return state.isConvsLoading;
    },
    [SkypeGetters.getMsgsLoading](state): boolean {
        return state.isMsgsLoading;
    },
    [SkypeGetters.getConvsPagination](state): SkypePagination {
        return state.convsPagination;
    },
    [SkypeGetters.getIsAllConvsLoaded](state): boolean {
        return state.isAllConvsLoaded;
    },
    [SkypeGetters.getSelectedConv](state): string {
        return state.selectedConv;
    },
    [SkypeGetters.getMsgGroupByConvId]: (state) => (convId: string): SkypeMsgGroup => {
        const conv = state.convs.find((c: SkypeConv) => c.id === convId);
        return (conv && state.msgs[convId]) ? state.msgs[conv.id].msgGroup : { isGroup: false, msgs: [] };
    },
    [SkypeGetters.getMsgsPaginationByConvId]: (state) => (convId: string): SkypePagination => {
        const conv = state.convs.find((c: SkypeConv) => c.id === convId);
        return (conv && state.msgs[convId]) ? state.msgs[conv.id].pagination : defaultPagination;
    },
    [SkypeGetters.getMsgsAllLoadedByConvId]: (state) => (convId: string): boolean => {
        const conv = state.convs.find((c: SkypeConv) => c.id === convId);
        return (conv && state.msgs[convId]) ? state.msgs[conv.id].isAllMsgLoaded : false;
    },
    [SkypeGetters.getSkypeUserList](state): string[] {
        return state.users;
    },
    [SkypeGetters.getIsUsersLoading](state): boolean {
        return state.isUsersLoading;
    },

}

export const mutations: MutationTree<SkypeState> = {
    [SkypeMutations.setSkypeConvs](state, payload: { skypeConvList: SkypeConv[], isAppend: boolean }): void {
        if (payload.isAppend) {
            state.convs = state.convs.concat(payload.skypeConvList);
        } else {
            state.convs = payload.skypeConvList;
        }
    },
    [SkypeMutations.setConvsLoading](state, payload: boolean): void {
        state.isConvsLoading = payload;
    },
    [SkypeMutations.setMsgsLoading](state, payload: boolean): void {
        state.isMsgsLoading = payload;
    },
    [SkypeMutations.setConvsPagination](state, payload: SkypePagination): void {
        state.convsPagination = payload;
    },
    [SkypeMutations.setIsAllConvsLoaded](state, payload: boolean): void {
        state.isAllConvsLoaded = payload;
    },
    [SkypeMutations.setMsgsByConvId](state, payload: { convId: string, skypeMsgGroup: SkypeMsgGroup, isAppend: boolean }): void {
        if (!state.msgs[payload.convId]) {
            state.msgs[payload.convId] = {
                msgGroup: { isGroup: false, msgs: [] },
                pagination: defaultPagination,
                isAllMsgLoaded: false,
            }
        }

        if (payload.isAppend) {
            state.msgs[payload.convId].msgGroup.msgs = state.msgs[payload.convId].msgGroup.msgs.concat(payload.skypeMsgGroup.msgs);
        } else {
            state.msgs[payload.convId].msgGroup.msgs = payload.skypeMsgGroup.msgs;
        }
    },
    [SkypeMutations.setMsgsPaginationByConvId](state, payload: { convId: string, skypePagination: SkypePagination }): void {
        if (!state.msgs[payload.convId]) {
            state.msgs[payload.convId] = {
                msgGroup: { isGroup: false, msgs: [] },
                pagination: defaultPagination,
                isAllMsgLoaded: false,
            }
        }

        state.msgs[payload.convId].pagination = payload.skypePagination;
    },
    [SkypeMutations.setMsgsAllLoadedByConvId](state, payload: { convId: string, isMsgAllLoaded: boolean }) {
        if (!state.msgs[payload.convId]) {
            state.msgs[payload.convId] = {
                msgGroup: { isGroup: false, msgs: [] },
                pagination: defaultPagination,
                isAllMsgLoaded: false,
            }
        }

        state.msgs[payload.convId].isAllMsgLoaded = payload.isMsgAllLoaded;
    },
    [SkypeMutations.setSelectedConv](state, payload: string): void {
        state.selectedConv = payload;
    },
    [SkypeMutations.resetAll](state): void {
        Object.assign(state, defaultState);
    },
    [SkypeMutations.setSkypeUserList](state, payload: string[]): void {
        state.users = payload;
    },
    [SkypeMutations.setIsUsersLoading](state, payload: boolean): void {
        state.isUsersLoading = payload;
    },

}

export const actions: ActionTree<SkypeState, RootState> = {
    async [SkypeActions.asyncGetSkypeUserList](
        { commit, state }: ActionContext<SkypeState, RootState>,
        { projId, uid }: { projId: string, uid: string }
    ): Promise<void> {
        try {
            commit(SkypeMutations.setIsUsersLoading, true);
            const skypeUserList = await skypeAPI.getSkypeUsers({ projId: projId, uid: uid });
            if (skypeUserList.length === 0) {
                commit(SkypeMutations.setIsAllConvsLoaded, true);
            } else {
                commit(SkypeMutations.setSkypeUserList, skypeUserList);
            }
        } catch (err) {
            console.log(err);
        } finally {
            commit(SkypeMutations.setIsUsersLoading, false);
        }

    },
    async [SkypeActions.asyncGetSkypeConvs](
        { commit, state }: ActionContext<SkypeState, RootState>,
        { projId, uid, userName, isAppend }: { projId: string, uid: string, userName: string, isAppend: boolean }
    ): Promise<void> {
        try {
            commit(SkypeMutations.setConvsLoading, true);
            const skypeConvList = await skypeAPI.getSkypeConvs({ projId: projId, uid: uid, userName: userName, ...state.convsPagination });
            if (skypeConvList.length !== 0) {
                commit(SkypeMutations.setSkypeConvs, { skypeConvList, isAppend });
            } else {
                commit(SkypeMutations.setIsAllConvsLoaded, true);
            }
        } catch (err) {
            commit(SkypeMutations.setIsAllConvsLoaded, true);
            console.log(err);
        } finally {
            commit(SkypeMutations.setConvsLoading, false);
        }

    },
    async [SkypeActions.asyncGetSkypeMsgs](
        { commit, getters }: ActionContext<SkypeState, RootState>,
        { projId, convId, uid, userName, isAppend }: { projId: string, convId: string, uid: string, userName: string, isAppend: boolean }
    ): Promise<void> {
        try {
            commit(SkypeMutations.setMsgsLoading, true);
            const pagination = getters[SkypeGetters.getMsgsPaginationByConvId](convId);
            const skypeMsgGroup = await skypeAPI.getSkypeMsgByConvId({ projId: projId, uid: uid, id: convId, userName: userName, ...pagination });
            if (skypeMsgGroup) {
                commit(SkypeMutations.setMsgsByConvId, { convId, skypeMsgGroup, isAppend });
                if (skypeMsgGroup.msgs.length < pagination.limit) {
                    commit(SkypeMutations.setMsgsAllLoadedByConvId, { convId: convId, isMsgAllLoaded: true });
                }
            } else {
                commit(SkypeMutations.setMsgsAllLoadedByConvId, { convId: convId, isMsgAllLoaded: true });
            }
        } catch (err) {
            commit(SkypeMutations.setMsgsAllLoadedByConvId, { convId: convId, isMsgAllLoaded: true });
            console.log(err);
        } finally {
            commit(SkypeMutations.setMsgsLoading, false);
        }
    }
}

export const skype: Module<SkypeState, RootState> = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
}
